import { I18n } from "@assets/strings";
import { Range } from "immutable";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { Feedback } from "@domain/group/feedback/feedback";
import { Group } from "@domain/group/group";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { Secondary } from "@ui/components/containers/textBlocks";
import { RoundedTextInput } from "@ui/components/inputs/textInput";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap } from "@ui/styles/utils";
import { Star } from "@ui/pages/lms/coursePage/feedback/star";

interface FeedbackModalProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
	feedback?: Feedback;
	close: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ close, shareroom, feedback, ...htmlProps }) => {
	const [score, setScore] = useState(1);
	const [hoverStars, setHoverStars] = useState<null | number>(null);
	const [comment, setComment] = useState("");
	const { groupService } = useServices();
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (feedback) {
			setScore(feedback.score);
			setComment(feedback.text ?? "");
		}
	}, [feedback]);

	return (
		<Container title={I18n.t("shareroom.review.infos")} close={close} lightTitle {...htmlProps}>
			<ShareroomTitle>{shareroom.name}</ShareroomTitle>
			<StarPicker onMouseLeave={() => setHoverStars(null)}>
				{Range(1, 6).map((n) => (
					<InteractiveStar
						key={n}
						$isFilled={hoverStars ? hoverStars >= n : score >= n}
						onMouseEnter={() => setHoverStars(n)}
						onClick={() => setScore(n)}
					/>
				))}
			</StarPicker>
			<Secondary>{I18n.t(`feedback.stars.${hoverStars ?? score}`)}</Secondary>
			<CommentInput
				value={comment}
				onChange={(e) => setComment(e.currentTarget.value)}
				placeholder={I18n.t("feedback.placeholder")}
			/>
			<LoadingButton
				loading={loading}
				onClick={async () => {
					try {
						setLoading(true);
						await groupService.sendFeedback(shareroom.id, {
							score,
							text: comment || undefined,
							groupFeedbackId: feedback?.id ?? undefined,
						});
						close();
					} catch {
						setLoading(false);
					}
				}}
			>
				{I18n.t("validate")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	align-items: center;
`;

const ShareroomTitle = styled.div`
	${({ theme }) => theme.textStyles.title};
	margin: 20px 0 35px;
`;

const CommentInput = styled(RoundedTextInput)`
	margin: 50px 0;
	line-height: 15px;
	font-size: 0.9375rem;
	padding: 20px 22px 19px;
	width: 100%;
	max-width: 500px;
	min-height: 130px;
`;

const StarPicker = styled.div`
	display: flex;
	${gap(12, "row")};
	margin-bottom: 10px;
`;

const InteractiveStar = styled(Star)<{ isFilled?: boolean }>`
	cursor: pointer;
`;
